import React, { useState, useEffect, useRef, useContext } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { CourseEditorContext } from '../../../../../contexts/CourseEditorContext';
import './ChapterEditor.scss';

export const TextComponent = ({ 
    content, 
    onContentChange, 
    isSelected, 
    onSelect, 
    id,
    fontSize,
    fontWeight,
    fontStyle,
    textDecoration,
    color,
    borderRadius,
    backgroundColor,
    opacity = 1,
    direction = 'rtl',
    mode = "edit",
    onAnimationComplete,
    isSequentialAnimationActive = false
}) => {
    const { i18n } = useContext(I18nReactContext);
    const courseEditorContext = useContext(CourseEditorContext);
    const [isEditing, setIsEditing] = useState(false);
    const [localContent, setLocalContent] = useState(content || '');
    const [textDirection, setTextDirection] = useState(direction);
    const [displayedText, setDisplayedText] = useState('');
    const [isAnimating, setIsAnimating] = useState(false);
    const textareaRef = useRef(null);
    const animationTimeoutRef = useRef(null);

    // Update local content when the prop changes
    useEffect(() => {
        setLocalContent(content || '');
    }, [content]);

    // Update direction when the prop changes
    useEffect(() => {
        setTextDirection(direction);
    }, [direction]);

    // Animation Effect - handles both typing and resize animations based on mode
    useEffect(() => {
        const newContent = content || '';
        const isAIGenerating = courseEditorContext?.isGeneratingChapter;
        const aiGenerationStatus = courseEditorContext?.aiGenerationStatus?.status;

        // Determine animation type based on the three scenarios:
        // 1. View mode for student: no typing animations, resize animation for all components
        // 2. AI not currently generating: no typing animations, resize animation for all components  
        // 3. AI currently generating: typing animation for text/bullets, resize for others

        const isStudentViewMode = mode === "view";
        const isAICurrentlyGenerating = isAIGenerating || (aiGenerationStatus === 'IN_PROGRESS');

        // Show typing animation only when:
        // - NOT in student view mode AND
        // - (AI is currently generating OR sequential animations are active) AND
        // - We have content AND
        // - AI generation is not completed
        const shouldShowTypingAnimation = !isStudentViewMode && 
                                        (isAICurrentlyGenerating || isSequentialAnimationActive) && 
                                        newContent && 
                                        aiGenerationStatus !== 'COMPLETED';

        if (shouldShowTypingAnimation) {
            // Clear any existing animation
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }

            setIsAnimating(true);
            setDisplayedText('');

            // Animate text character by character
            let currentIndex = 0;
            const animateText = () => {
                if (currentIndex < newContent.length) {
                    setDisplayedText(newContent.substring(0, currentIndex + 1));
                    currentIndex++;
                    animationTimeoutRef.current = setTimeout(animateText, 20); // 20ms per character
                } else {
                    setIsAnimating(false);
                    // Call animation completion callback for sequential animations
                    if (isSequentialAnimationActive && onAnimationComplete) {
                        onAnimationComplete(id);
                    }
                }
            };

            // Start animation after a brief delay
            animationTimeoutRef.current = setTimeout(animateText, 100);
        } else {
            // For all other scenarios (student view, AI not generating, AI completed):
            // Show full content immediately and let CSS handle resize animation
            setDisplayedText(newContent);
            setIsAnimating(false);

            // If sequential animations are active but we're not showing typing animation,
            // immediately call completion callback
            if (isSequentialAnimationActive && onAnimationComplete && newContent) {
                setTimeout(() => onAnimationComplete(id), 50);
            }
        }

        // Cleanup function
        return () => {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, [content, courseEditorContext?.isGeneratingChapter, courseEditorContext?.isShowingAIChapterAnimations, courseEditorContext?.aiGenerationStatus?.status, isEditing, mode, isSequentialAnimationActive, onAnimationComplete, id]);

    // Focus the textarea when editing starts
    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isEditing]);

    // Toggle text direction
    const toggleDirection = () => {
        const newDirection = textDirection === 'ltr' ? 'rtl' : 'ltr';
        setTextDirection(newDirection);
        // Notify parent component of direction change
        if (onContentChange) {
            onContentChange(localContent, { direction: newDirection });
        }
    };

    const handleClick = (e) => {
        e.stopPropagation();
        if (!isSelected) {
            onSelect(id);
        }
    };

    const handleDoubleClick = (e) => {
        // Only start editing if we're not dragging and we're not in view mode
        // This helps prevent conflicts between dragging and editing
        if (!window.dragInProgress && mode !== "view") {
            e.stopPropagation();
            setIsEditing(true);
        }
    };

    const handleChange = (e) => {
        setLocalContent(e.target.value);
    };

    const handleBlur = () => {
        setIsEditing(false);
        onContentChange(localContent, { direction: textDirection });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            // Ctrl+Enter to save and exit editing
            setIsEditing(false);
            onContentChange(localContent, { direction: textDirection });
        } else if (e.key === 'Escape') {
            // Escape to cancel editing
            setIsEditing(false);
            setLocalContent(content || '');
        } else if ((e.key === 'Shift' && e.altKey) || (e.key === 'Alt' && e.shiftKey)) {
            // Alt+Shift to toggle text direction
            e.preventDefault();
            toggleDirection();
        }
    };

    // Create a style object for the text content
    const textStyle = {
        fontSize: fontSize ? `${fontSize}px` : undefined,
        fontWeight,
        fontStyle,
        textDecoration,
        color
    };

    // Parse border radius values or use defaults
    const topLeft = borderRadius?.topLeft || 0;
    const topRight = borderRadius?.topRight || 0;
    const bottomLeft = borderRadius?.bottomLeft || 0;
    const bottomRight = borderRadius?.bottomRight || 0;

    // Create style object for the container
    const containerStyle = {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        // overflow: 'hidden',
        position: 'relative',
        borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
        padding: '8px',
        direction: textDirection,
    };

    // Create style for the background with opacity
    const backgroundStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: backgroundColor || 'transparent',
        opacity: opacity,
        borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
        zIndex: -1, // Ensure background is behind the text content
    };

    return (
        <div 
            className={`TextComponent ${isSelected ? 'selected' : ''}`}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
        >
            <div className="text-container" style={containerStyle}>
                {/* Background div with opacity */}
                <div style={backgroundStyle}></div>


                {isEditing ? (
                    <textarea
                        ref={textareaRef}
                        value={localContent}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        className="text-editor"
                        placeholder={i18n.translate('weezbeeCourse.editor.textComponent.placeholder')}
                        style={{...textStyle, width: '100%', height: '100%', border: 'none', direction: textDirection}}
                    />
                ) : (
                    <div className="text-content" style={{...textStyle, width: '100%', height: '100%', direction: textDirection}}>
                        {localContent ? (
                            <span>
                                {isAnimating ? displayedText : localContent}
                                {isAnimating && <span className="ai-typing-cursor">|</span>}
                            </span>
                        ) : mode !== "view" ? (
                            <span className="placeholder">{i18n.translate('weezbeeCourse.editor.textComponent.emptyState')}</span>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
};
